<!--
 * @Descripttion: 终端 / 雇员合同模版 新建·编辑弹窗（向日葵服务助手专用）
 *   - 新建必传模版文件；编辑不强制重传
 *   - 合同有效期：起 ≤ 止；勾选「长期有效」时 start/end 置空
 *   - 终端人员合同启用为同一时间只能一份：保存时由后端互斥处理，返回 disabledCount
-->
<template>
  <BaseDialog
    custom-class="terminal-template-dialog"
    :title="isEdit ? '编辑模版' : '新建模版'"
    width="560px"
    :model-value="true"
    @close="onCancel"
  >
    <div style="padding: 20px">
      <BaseForm
        ref="formRef"
        :model="formData"
        :rules="rules"
        label-width="120px"
        label-position="right"
      >
        <BaseFormItem label="模版名称" prop="contractTemplateName">
          <BaseInput
            v-model="formData.contractTemplateName"
            maxlength="50"
            show-word-limit
            placeholder="请输入模版名称（最多 50 字）"
          />
        </BaseFormItem>
        <BaseFormItem label="模版文件" prop="contractTemplateUrl">
          <BaseUpload
            ref="uploadRef"
            v-model:fileList="formData.contractTemplateUrl"
            :autoUpload="true"
            :http-request="uploadApi"
            :accept="['.pdf', '.doc', '.docx']"
            :limit="1"
            :limitSize="20"
            :onError="onUploadError"
          >
            <template #default>
              <BaseButton type="primary2">点击上传</BaseButton>
              <span class="upload-hint">
                支持 pdf / doc / docx，不超过 20MB
              </span>
            </template>
          </BaseUpload>
        </BaseFormItem>

        <BaseFormItem label="合同有效期" prop="validity">
          <BaseDatepickerLongterm
            style="width: 100%; margin-top: 1px"
            startPlaceholder="有效期始"
            endPlaceholder="有效期止"
            v-model:value="formData.validity"
            @update:value="handleValidityChange"
          />
        </BaseFormItem>

        <BaseFormItem label="状态" prop="enabled">
          <BaseSwitch
            v-model="formData.enabled"
            :active-value="TerminalTemplateStatus.ENABLED"
            :inactive-value="TerminalTemplateStatus.DISABLED"
            active-text="启用"
            inactive-text="停用"
          />
        </BaseFormItem>

        <BaseFormItem label="备注" prop="remarks">
          <BaseInput
            style="width: 300px"
            v-model="formData.remarks"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入备注（≤200 字）"
          />
        </BaseFormItem>
      </BaseForm>
    </div>

    <template #footer>
      <BaseButton type="default" @click="onCancel">取消</BaseButton>
      <BaseButton :loading="saving" :click="() => onSubmit(1)">保存</BaseButton>
    </template>
  </BaseDialog>
</template>

<script lang="tsx" setup>
import { computed, ref, onMounted } from 'vue';
import {
  BaseDialog,
  BaseForm,
  BaseFormItem,
  BaseInput,
  BaseSwitch,
  BaseUpload,
  BaseFormRef,
  BaseButton,
  XrkMessageBox,
  XrkMessage
} from 'xrk-components';

import { uploadApi } from '@/common/data-source/common/apis';
import { saveTerminalTemplate, getTerminalTemplate } from '../data-source/apis';
import type { TerminalTemplateRow } from '../data-source/types';
import { TerminalTemplateStatus } from '../constants';
import { BaseDatepickerLongterm } from '@/components/base/base-datepicker-longterm/index';

const props = defineProps<{
  remove: () => void;
  /** 传入即为编辑 */
  id?: number;
}>();
const emits = defineEmits<{ (e: 'success', disabledCount?: number): void }>();

const isEdit = computed(() => props.id != null);

const formRef = ref<BaseFormRef>(null);
const saving = ref(false);

const formData = ref({
  contractTemplateName: '',
  contractTemplateUrl: [],
  urlFileName: '',
  validity: [],
  enabled: TerminalTemplateStatus.ENABLED,
  remarks: ''
});

const rules = {
  contractTemplateName: [
    { required: true, message: '请输入模版名称', trigger: 'blur' }
  ],
  contractTemplateUrl: [
    {
      required: true,
      validator: (_, __, cb) => {
        if (!isEdit.value && !formData.value.contractTemplateUrl.length) {
          return cb(new Error('请上传模版文件'));
        }
        cb();
      },
      trigger: 'change'
    }
  ],
  validity: [
    {
      required: true,
      validator: (_rule: any, value: any, callback: any) => {
        if (!value?.length) {
          callback(new Error('合同有效期不能为空'));
        } else {
          callback();
        }
      },
      trigger: 'change'
    }
  ]
};

const handleValidityChange = () => {
  formRef.value.validateField('validity');
};

const onUploadError = (info: { msg: string }) => {
  XrkMessage.error(info.msg);
};

const loadDetail = async () => {
  if (!isEdit.value || !props.id) return;
  const { data } = await getTerminalTemplate({ id: props.id });
  formData.value = {
    contractTemplateName: data.contractTemplateName,
    contractTemplateUrl: [
      { url: data.contractTemplateUrl, name: data.urlFileName }
    ],
    urlFileName: data.urlFileName,
    validity: data.validityStart ? [data.validityStart, data.validityEnd] : [],
    enabled: data.enabled,
    remarks: data.remarks || ''
  };
};

onMounted(loadDetail);

const onCancel = () => props.remove();

const onSubmit = async (check: number) => {
  await formRef.value?.validate();

  saving.value = true;
  try {
    const contractTemplateUrl = formData.value.contractTemplateUrl[0]?.url;
    const urlFileName = formData.value.contractTemplateUrl[0]?.name;

    const payload: Omit<TerminalTemplateRow, 'id' | 'createAt'> & {
      id?: number;
    } = {
      ...(isEdit.value ? { id: props.id } : {}),
      contractTemplateName: formData.value.contractTemplateName,
      contractTemplateUrl,
      urlFileName,
      validityStart: formData.value.validity?.[0] || null,
      validityEnd: formData.value.validity?.[1] || null,
      enabled: formData.value.enabled,
      remarks: formData.value.remarks,
      type: 5
    };
    saveTerminalTemplate({ ...payload, check }).then(async ({ data }) => {
      if (+data === 7778) {
        XrkMessageBox.confirm(
          '已存在该模版，是否继续添加，如添加则原合同将会失效',
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消'
          }
        ).then(async () => {
          onSubmit(0);
        });
      } else {
        emits('success');
        props.remove();
      }
    });
  } finally {
    saving.value = false;
  }
};
</script>

<style lang="scss" scoped>
.upload-hint {
  margin-left: 8px;
  font-size: 12px;
  color: #9ca3af;
}
</style>
